import React from 'react'
import { graphql } from 'babel-plugin-relay/macro'
import { useLazyLoadQuery } from 'react-relay-offline'

import type {
  AdvertisementsQuery,
  AdvertisementsQuery$data,
} from './__generated__/AdvertisementsQuery.graphql'
import { Error, EmptyState, SectionsRender } from './components'
import { DefaultTopbar } from '../../../../core/components/TopbarVariations'
import { useVendor } from '../../../../core/globalState/vendor'
import type { ConfigurationDataSetType } from './types'
import { PageAdvertisements } from './components/PageAdvertisements'

const ADVERTISEMENTS_QUERY = graphql`
  query AdvertisementsQuery($filters: ContentsFiltersInput) {
    contents(filters: $filters) {
      edges {
        node {
          id
          name
          variants {
            edges {
              node {
                id
                sections {
                  id
                  name
                  data
                }
                configurationDataSets {
                  configurations {
                    data
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

const formatAdvertisementsData = (
  data?: AdvertisementsQuery['response']['contents']['edges'] | null
): ConfigurationDataSetType[] | undefined => {
  return (
    data?.flatMap((dataEdge) =>
      dataEdge.node.variants?.edges.map((edge) => {
        return {
          id: edge.node.id,
          configurationDataSets: edge.node.configurationDataSets || [],
          sections: edge.node.sections || [],
        }
      })
    ) || []
  )
}

const getConfigurationDataByStoreId = (
  storeId: string,
  dataConfigurationDataSet: ConfigurationDataSetType[] | undefined
) => {
  if (!dataConfigurationDataSet) return []

  const configurationByStore = dataConfigurationDataSet.filter((dataEdge) =>
    dataEdge?.configurationDataSets?.some((configurationDataSet) =>
      configurationDataSet.configurations?.some(
        (configuration) =>
          typeof configuration.data === 'object' &&
          configuration.data !== null &&
          Object.values(configuration.data).some(
            (value: any) => value === storeId
          )
      )
    )
  )

  return configurationByStore
}

const getAccountSections = (
  data?: AdvertisementsQuery['response']['contents']['edges'] | null
) => {
  return (
    data?.flatMap((edge) =>
      (edge.node.variants?.edges ?? []).flatMap((variantEdge) =>
        variantEdge.node.configurationDataSets &&
        variantEdge.node.configurationDataSets.length === 0
          ? (variantEdge.node.sections ?? []).map((section) => ({
              id: section.id,
              name: section.name,
              data: section.data,
            }))
          : []
      )
    ) || []
  )
}

const getSectionsByStoreId = (
  storeId: string,
  data?: AdvertisementsQuery['response']['contents']['edges'] | null
) => {
  const formattedDataEdge = formatAdvertisementsData(data)
  const configurationByStore = getConfigurationDataByStoreId(
    storeId,
    formattedDataEdge
  )

  return (
    configurationByStore?.flatMap((variantEdge) =>
      (variantEdge?.sections ?? []).flatMap((section) => ({
        id: section.id,
        name: section.name,
        data: section.data,
      }))
    ) ?? []
  )
}

const getSections = (
  data: AdvertisementsQuery$data | undefined | null,
  storeId: string
) => {
  const storeSections = getSectionsByStoreId(storeId, data?.contents.edges)

  if (storeSections.length > 0) {
    return storeSections
  }

  return getAccountSections(data?.contents.edges)
}

export const AdvertisementsScreen = () => {
  const { vendor } = useVendor()

  const { data, error } = useLazyLoadQuery<AdvertisementsQuery>(
    ADVERTISEMENTS_QUERY,
    {
      filters: {
        status: {
          published: true,
        },
        builderId: {
          value: 'assisted-sales-app',
        },
        contentTypes: {
          ids: [
            {
              value: 'advertisements',
            },
            {
              value: 'advertisementsByStore',
            },
          ],
        },
      },
    },
    {
      fetchPolicy: 'store-and-network',
    }
  )

  const sections = getSections(data, vendor.storeId)

  const handleTryAgainClick = () => {
    window.location.reload()
  }

  if (error) {
    return (
      <>
        <DefaultTopbar />
        <Error onTryAgainClick={handleTryAgainClick} />
      </>
    )
  }

  if (!sections.length) {
    return (
      <>
        <DefaultTopbar />
        <EmptyState />
      </>
    )
  }

  return (
    <PageAdvertisements>
      <DefaultTopbar />
      <SectionsRender sections={sections} />
    </PageAdvertisements>
  )
}
